import { useEffect, useRef, useCallback } from "react";
import { useMap } from "react-leaflet";
import API, { MAP_DATA_TIMEOUT } from "../../utils/api";
import { tError } from "../../utils/toast";

const DEBOUNCE_MS = 320;

/**
 * Fetches leads/map-data with bbox from the current Leaflet view.
 * Parent must only render when viewport mode is enabled.
 */
export default function MapViewportFetcher({
  territoryId,
  refreshTick = 0,
  onApply,
  setLoading,
}) {
  const map = useMap();
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  const doFetch = useCallback(async () => {
    if (!map) return;
    const b = map.getBounds();
    const params = {
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    };
    if (territoryId != null && Number.isFinite(territoryId)) {
      params.territory = territoryId;
    }

    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await API.get("leads/map-data/", {
        params,
        timeout: MAP_DATA_TIMEOUT,
      });
      if (reqId !== requestIdRef.current) return;
      onApplyRef.current(res?.data);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.error("Viewport map fetch failed:", err);
      onApplyRef.current([]);
      const isTimeout = err?.code === "ECONNABORTED";
      tError(
        isTimeout
          ? "Loading leads timed out — try again or check the API server."
          : "Failed to load leads for map view"
      );
    } finally {
      if (reqId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [map, territoryId, setLoading]);

  const scheduleDebouncedFetch = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doFetch();
    }, DEBOUNCE_MS);
  }, [doFetch]);

  // Lead sync / explicit invalidation
  useEffect(() => {
    doFetch();
  }, [refreshTick, doFetch]);

  useEffect(() => {
    if (!map) return;
    const onMove = () => scheduleDebouncedFetch();
    map.on("moveend", onMove);
    map.on("zoomend", onMove);
    return () => {
      map.off("moveend", onMove);
      map.off("zoomend", onMove);
      clearTimeout(debounceRef.current);
    };
  }, [map, scheduleDebouncedFetch]);

  return null;
}
